import {
  Box,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  Button,
} from "@mui/material";
import "./banking.css";
import { get, noop } from "lodash";
import {
  AcceptNiumTermsAndConditions,
  FetchNiumTermsAndConditions,
} from "store/reducers/companyBankingSettings";
import { useSelector, useDispatch } from "react-redux";
import CashDDialog from "components/common/CashDDialog";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { fetchCompanyDetails } from "store/reducers/companyTabDetails";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import CashDCricularProgress from "components/common/CashDCricularProgress";

interface CorporateCustomerTermsAndConditionsProps {
  open: boolean;
  lenderId: string;
  lenderName: string;
  onClose: typeof noop;
  openLenderConfirmationModal: typeof noop;
}

const CorporateCustomerTermsAndConditionsModal: React.FC<
  CorporateCustomerTermsAndConditionsProps
> = ({ open, lenderId, lenderName, onClose, openLenderConfirmationModal }) => {
  const dispatch = useDispatch();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const selectedCompany = useSelector(
    (state: any) => state.dashboardSearch.selectedCompany
  );
  const selectedCompanyId = get(selectedCompany, "_id", "");
  const { niumTermsAndConditions } =
    useSelector((state: any) => state.bankingSettingsTabData) || {};
  const { name, versionId } = niumTermsAndConditions || {};

  useEffect(() => {
    dispatch(FetchNiumTermsAndConditions(selectedCompanyId, lenderId));
  }, [dispatch, lenderId, selectedCompanyId]);

  // Memoize the terms content to prevent unnecessary re-renders
  const termsContent = useMemo(
    () => ({
      __html: niumTermsAndConditions?.description,
    }),
    [niumTermsAndConditions]
  );

  // Handle checkbox change with useCallback
  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTermsAccepted(event.target.checked);
    },
    []
  );

  const acceptTermsAndConditions = useCallback(async () => {
    if (!name || !versionId) return;

    const payload = {
      accept: true,
      name,
      versionId,
    };

    await dispatch(
      AcceptNiumTermsAndConditions(selectedCompanyId, lenderId, payload)
    );
    await dispatch(fetchCompanyDetails(selectedCompanyId));
    openLenderConfirmationModal(true);
  }, [name, versionId, dispatch, selectedCompanyId, lenderId, openLenderConfirmationModal]);

  return (
    <CashDDialog open={open} handleClose={onClose}>
      <Box className="header-box">
        <Typography variant="h4" className="view-table-heading">
          Terms Conditions for
          <span className="lender_name">{lenderName}</span>
        </Typography>
        <IconButton onClick={onClose}>
          <CancelOutlinedIcon />
        </IconButton>
      </Box>
      {name && versionId ? (
        <>
          <Box
            sx={{ padding: 2, maxHeight: "80vh", overflowY: "auto" }}
            dangerouslySetInnerHTML={termsContent}
          />
          <Box sx={{ padding: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label="I agree to the Terms and Conditions"
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                onClick={acceptTermsAndConditions}
                disabled={!termsAccepted}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </>
      ) : (
        <CashDCricularProgress />
      )}
    </CashDDialog>
  );
};

export default React.memo(CorporateCustomerTermsAndConditionsModal);
